// The interactive-session voice controls, combined into ONE bordered card (caption + mic/speaker/
// End pills + optional type-to-SAM input) instead of three separate floating pieces — matches the
// "Voice composer" reference mockup, where cap/row/bar all share a single box. While connecting,
// the card instead shows VoiceConnecting; its border tints accent while that's happening, same as
// the mockup's .box.connecting.
import { CaptionBox } from '@/components/chat/CaptionBox';
import { VoicePills } from './VoicePills';
import { VoiceConnecting } from './VoiceConnecting';
import { InteractiveTextInput } from './InteractiveTextInput';
import type { AudioMoreMenuProps } from './AudioMoreMenu';

export const VoiceComposer = ({
  connecting,
  reconnecting = false,
  onCancelConnect,
  caption,
  speaking,
  recording,
  micMuted,
  onToggleMic,
  speakerMuted,
  onToggleSpeaker,
  moreMenu,
  onEnd,
  textInputEnabled,
  onSendText,
}: {
  connecting: boolean;
  /** The socket dropped mid-session: the mic is not transmitting until it reconnects. */
  reconnecting?: boolean;
  onCancelConnect: () => void;
  /** Omitted entirely on surfaces that show the reply as a thread bubble instead (desktop
   *  interactive); passed on surfaces where the live caption belongs in the voice card. */
  caption?: { text: string; show: boolean };
  /** SAM's voice is actually sounding right now — shows the animated equalizer in the caption and
   *  highlights the Speaker pill. */
  speaking: boolean;
  recording: boolean;
  micMuted?: boolean;
  onToggleMic?: () => void;
  speakerMuted?: boolean;
  onToggleSpeaker?: () => void;
  moreMenu?: AudioMoreMenuProps;
  onEnd?: () => void;
  textInputEnabled: boolean;
  onSendText: (text: string) => boolean | void;
}) => (
  <div
    className={`mx-4 mb-2 rounded-2xl border bg-ui-subtle overflow-hidden transition-colors ${
      connecting ? 'border-accent/30' : reconnecting ? 'border-warning/40' : 'border-ui-border'
    }`}
  >
    {connecting ? (
      <VoiceConnecting onCancel={onCancelConnect} />
    ) : (
      <>
        {reconnecting && (
          <p role="status" aria-live="polite" className="px-4 pt-3 text-caption text-warning-text">
            Connection lost — reconnecting… SAM can’t hear you until it’s back.
          </p>
        )}
        {caption && <CaptionBox variant="bare" text={caption.text} show={caption.show} tone="assistant" speaking={speaking} />}
        <div className="pt-3">
          <VoicePills
            recording={recording}
            speaking={speaking}
            micMuted={micMuted}
            onToggleMic={onToggleMic}
            speakerMuted={speakerMuted}
            onToggleSpeaker={onToggleSpeaker}
            moreMenu={moreMenu}
            onEnd={onEnd}
          />
        </div>
        {textInputEnabled && <InteractiveTextInput bare onSend={onSendText} wrapperClassName="px-4 pt-1 pb-3" />}
      </>
    )}
  </div>
);
